import { routeMillGantt } from "@/setup/routes/mill.routes";
import {
	ErrorModal,
	HelmetDexteel,
	ShiftDayNavigatorControl,
	useShiftNavigator,
	useUserContext,
} from "@dexteel/mesf-core";
import { Divider, Grid2, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { get } from "lodash-es";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { pageBgColor } from "@/utils/theme";
import { GanttChart } from "./components/GanttChart";
import RealTimeViewer from "./components/RealTimeViewer";
import RealTimeViewer2 from "./components/RealTimeViewer2";
import { ShiftChart } from "./components/ShiftChart";
import { GanttReponse } from "./models/GanttReponse";
import { getGanttData } from "./repositories/repositories";

type useGanttProps = {
	start: Date | undefined;
	end: Date | undefined;
	onError: (error: Error) => void;
	isModalOpen?: boolean;
	enabled?: boolean;
};

export const useGanttData = ({
	start,
	end,
	onError,
	isModalOpen,
	enabled = true,
}: useGanttProps) => {
	const {
		state: { defaultAreaId },
	} = useUserContext();
	const assetId = Number(defaultAreaId);

	return useQuery<GanttReponse, Error>({
		queryKey: ["gantt", start, end, assetId],
		meta: { realtime: ["M2.GetGanttData"] },
		queryFn: ({ signal }) => getGanttData({ start, end, assetId }, signal),
		onError: onError,
		refetchInterval: isModalOpen ? false : 30_000,
		enabled: enabled,
	});
};

export const DashboardPage = () => {
	const [error, setError] = useState("");
	const [isModalOpen, setIsModalOpen] = useState(false);

	const {
		state: { defaultAreaId },
	} = useUserContext();
	const assetId = Number(defaultAreaId);

	const navigate = useNavigate();
	const { shiftId: shiftIdParam } = useParams();
	const [searchParams] = useSearchParams();
	const highlightedCarrierInstanceId = searchParams.get("carrierInstanceId")
		? Number(searchParams.get("carrierInstanceId"))
		: null;

	const { shiftInfo, getShiftDataFromAPI } = useShiftNavigator();

	const {
		data: ganttData,
		isLoading,
		refetch,
	} = useGanttData({
		start: shiftInfo?.CurrentStart,
		end: shiftInfo?.CurrentEnd,
		onError: (e) => setError(e.message),
		isModalOpen: isModalOpen,
		enabled: !!shiftInfo?.CurrentStart && !!shiftInfo?.CurrentEnd,
	});

	useEffect(() => {
		if (shiftInfo) {
			const shiftId = get(shiftInfo, "CurrentShiftId", null);
			const queryString = searchParams.toString();
			navigate(
				`${routeMillGantt}/${shiftId}${queryString ? `?${queryString}` : ""}`,
				{ replace: true },
			);
		}
	}, [shiftInfo]);

	useEffect(() => {
		if (!assetId) return;
		(async () => {
			if (shiftIdParam) {
				await getShiftDataFromAPI(null, Number(shiftIdParam), assetId);
				return;
			}
			const productionDate = shiftInfo?.CurrentProductionDate ?? null;
			await getShiftDataFromAPI(productionDate, 0, assetId);
		})();
	}, [assetId]);

	return (
		<>
			<HelmetDexteel title="2 Mill Production Timeline" />
			<Grid2
				height={"91vh"}
				px={2}
				pt={2}
				sx={{ backgroundColor: pageBgColor }}
			>
				<Grid2 container spacing={1} size={{ xs: 12, md: 12 }}>
					<Grid2 container justifyContent="space-between" alignItems="center">
						<Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4 }}>
							<Grid2 container direction="column" spacing={1}>
								<Grid2>
									<Typography variant="h5" fontWeight={600}>
										2 Mill Production Timeline
									</Typography>
								</Grid2>
								<Grid2 maxWidth={"fit-content"}>
									<ShiftDayNavigatorControl preventInit assetId={assetId} />
								</Grid2>
							</Grid2>
						</Grid2>

						<Grid2
							size={{ xs: 12, sm: 12, md: 8, lg: 8 }}
							container
							justifyContent="flex-end"
							alignItems="center"
							spacing={1}
						>
							<Grid2 size={{ xs: 12, sm: 4, md: 4.5 }}>
								<ShiftChart />
							</Grid2>

							<Grid2 size={{ xs: 12, sm: 4, md: "auto" }}>
								<RealTimeViewer
									delayPercent={ganttData?.tables[4].rows[0]?.DelayTimePercent}
									productionPercent={
										ganttData?.tables[4].rows[0]?.ProductionTimePercent
									}
									tph={ganttData?.tables[4].rows[0]?.TPH}
								/>
							</Grid2>

							<Grid2 size={{ xs: 12, sm: 4, md: "auto" }}>
								<RealTimeViewer2
									totalAdjustments={ganttData?.tables[2].rows.length || 0}
									totalTons={ganttData?.tables[4].rows[0]?.TotalTons}
									totalCobbles={ganttData?.tables[4].rows[0]?.TotalCobbles}
								/>
							</Grid2>
						</Grid2>
					</Grid2>
					<Grid2 size={{ xs: 12 }}>
						<Divider />
					</Grid2>
					<GanttChart
						isLoading={isLoading}
						refetch={refetch}
						start={shiftInfo?.CurrentStart!}
						end={shiftInfo?.CurrentEnd!}
						productionData={ganttData?.tables[0].rows || []}
						delayData={ganttData?.tables[1].rows || []}
						adjustmentData={ganttData?.tables[2].rows || []}
						carrierRealData={ganttData?.tables[3].rows || []}
						sectionEventStatsData={ganttData?.tables[5].rows || []}
						cobbleData={ganttData?.tables[6].rows || []}
						passChangeData={ganttData?.tables[7].rows || []}
						rollChangeData={ganttData?.tables[8].rows || []}
						dmhEventData={ganttData?.tables[9]?.rows || []}
						setIsModalOpen={setIsModalOpen}
						highlightedCarrierInstanceId={highlightedCarrierInstanceId}
					/>
				</Grid2>
			</Grid2>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
